import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminAPI, projectsAPI, leaveAPI, availabilityAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Building2,
  FolderOpen,
  FileText,
  Clock,
  AlertCircle,
  ArrowRight,
  Mail,
  Calendar,
  Sun,
  Moon,
  Sunset,
  Plus,
  Loader2,
  Check,
  X,
  LayoutDashboard,
} from 'lucide-react';
import { formatDateTime, formatDate, getStatusLabel, getEventTypeLabel } from '../../lib/utils';
import { toast } from 'sonner';

// Get greeting based on time of day
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return { text: 'Bonjour', icon: Sun, color: 'text-amber-500' };
  if (hour < 18) return { text: 'Bon après-midi', icon: Sunset, color: 'text-orange-500' };
  return { text: 'Bonsoir', icon: Moon, color: 'text-indigo-500' };
};

const getLeaveTypeLabel = (type) => {
  const labels = {
    vacances: 'Vacances',
    maladie: 'Maladie',
    personnel: 'Personnel',
    autre: 'Autre',
  };
  return labels[type] || type;
};

const getLeaveStatusLabel = (status) => {
  const labels = {
    en_attente: 'En attente',
    approuve: 'Approuvé',
    refuse: 'Refusé',
  };
  return labels[status] || status;
};

const getLeaveStatusColor = (status) => {
  switch (status) {
    case 'approuve': return 'bg-green-100 text-green-800';
    case 'refuse': return 'bg-red-100 text-red-800';
    default: return 'bg-amber-100 text-amber-800';
  }
};

const DashboardPage = () => {
  const { user, isStaff, isClient, isAdmin, isEmployee } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [newLeave, setNewLeave] = useState({
    type_conge: 'vacances',
    date_debut: '',
    date_fin: '',
    motif: '',
  });
  
  const [editAvailability, setEditAvailability] = useState({
    lundi: '09:00-17:00',
    mardi: '09:00-17:00',
    mercredi: '09:00-17:00',
    jeudi: '09:00-17:00',
    vendredi: '09:00-17:00',
    notes: '',
  });

  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (isStaff) {
          const response = await adminAPI.getDashboard();
          setDashboardData(response.data);
          
          // Fetch leave requests and availability for employees
          if (isEmployee || isAdmin) {
            const [leavesRes, availRes] = await Promise.all([
              leaveAPI.getAll(),
              availabilityAPI.getMine(),
            ]);
            setLeaveRequests(leavesRes.data);
            setAvailability(availRes.data);
            setEditAvailability({
              lundi: availRes.data?.lundi || '09:00-17:00',
              mardi: availRes.data?.mardi || '09:00-17:00',
              mercredi: availRes.data?.mercredi || '09:00-17:00',
              jeudi: availRes.data?.jeudi || '09:00-17:00',
              vendredi: availRes.data?.vendredi || '09:00-17:00',
              notes: availRes.data?.notes || '',
            });
          }
        }
        const projectsResponse = await projectsAPI.getAll();
        setProjects(projectsResponse.data);
      } catch (error) {
        console.error('Erreur chargement données:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isStaff, isEmployee, isAdmin]);

  const handleCreateLeave = async () => {
    if (!newLeave.date_debut || !newLeave.date_fin) {
      toast.error('Veuillez sélectionner les dates');
      return;
    }
    setSaving(true);
    try {
      await leaveAPI.create(newLeave);
      toast.success('Demande de congé soumise');
      setLeaveDialogOpen(false);
      setNewLeave({ type_conge: 'vacances', date_debut: '', date_fin: '', motif: '' });
      const res = await leaveAPI.getAll();
      setLeaveRequests(res.data);
    } catch (error) {
      toast.error('Erreur lors de la soumission');
    } finally {
      setSaving(false);
    }
  };

  const handleReviewLeave = async (leaveId, status) => {
    try {
      await leaveAPI.review(leaveId, status);
      toast.success(status === 'approuve' ? 'Congé approuvé' : 'Congé refusé');
      const res = await leaveAPI.getAll();
      setLeaveRequests(res.data);
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleUpdateAvailability = async () => {
    setSaving(true);
    try {
      await availabilityAPI.update(editAvailability);
      toast.success('Disponibilités mises à jour');
      setAvailabilityDialogOpen(false);
      const res = await availabilityAPI.getMine();
      setAvailability(res.data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid md:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Client Dashboard
  if (isClient) {
    return (
      <div className="space-y-6" data-testid="client-dashboard">
        <div className="flex items-center gap-3">
          <GreetingIcon className={`w-8 h-8 ${greeting.color}`} />
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              {greeting.text}, {user?.prenom}!
            </h1>
            <p className="text-slate-600">
              Consultez vos projets et documents ci-dessous.
            </p>
          </div>
        </div>

        {projects.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FolderOpen className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">Aucun projet</h3>
              <p className="text-slate-600 mb-4">
                Vous n'avez pas encore de projets assignés.
              </p>
              <Link to="/contact">
                <Button>Nous contacter</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Link key={project.id} to={`/portail/projets/${project.id}`}>
                <Card className="hover:border-primary/30 hover:shadow-md transition-all cursor-pointer h-full">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <FolderOpen className="w-5 h-5 text-primary" />
                      </div>
                      <Badge variant={project.statut === 'actif' ? 'default' : 'secondary'}>
                        {getStatusLabel(project.statut)}
                      </Badge>
                    </div>
                    <h3 className="font-heading font-semibold text-lg mb-2">{project.nom}</h3>
                    <p className="text-slate-600 text-sm line-clamp-2 mb-4">
                      {project.description || 'Aucune description'}
                    </p>
                    <div className="flex items-center text-sm text-slate-500">
                      <ArrowRight className="w-4 h-4 mr-1" />
                      Voir le projet
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Staff/Employee Dashboard
  return (
    <div className="space-y-6" data-testid="staff-dashboard">
      {/* Personalized Greeting */}
      <div className="flex items-center gap-3">
        <GreetingIcon className={`w-8 h-8 ${greeting.color}`} />
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            {greeting.text}, {user?.prenom}!
          </h1>
          <p className="text-slate-600">
            Vue d'ensemble de l'activité du portail.
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboardData?.stats?.clients || 0}</p>
                <p className="text-xs text-slate-500">Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FolderOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboardData?.stats?.projets || 0}</p>
                <p className="text-xs text-slate-500">Projets</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboardData?.stats?.fichiers || 0}</p>
                <p className="text-xs text-slate-500">Fichiers</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboardData?.stats?.fichiers_en_attente || 0}</p>
                <p className="text-xs text-slate-500">En attente</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboardData?.stats?.contacts_non_traites || 0}</p>
                <p className="text-xs text-slate-500">Contacts</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Employee Section: Leave Requests & Availability */}
      {(isEmployee || isAdmin) && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Leave Requests */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Demandes de congés</CardTitle>
              <Button size="sm" onClick={() => setLeaveDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Nouvelle demande
              </Button>
            </CardHeader>
            <CardContent>
              {leaveRequests.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Aucune demande de congé</p>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {leaveRequests.slice(0, 5).map((leave) => (
                    <div key={leave.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-sm">
                            {getLeaveTypeLabel(leave.type_conge)}
                          </span>
                          {isAdmin && leave.user_name && (
                            <span className="text-xs text-slate-500">- {leave.user_name}</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          {formatDate(leave.date_debut)} → {formatDate(leave.date_fin)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={getLeaveStatusColor(leave.statut)}>
                          {getLeaveStatusLabel(leave.statut)}
                        </Badge>
                        {isAdmin && leave.statut === 'en_attente' && (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-green-600"
                              onClick={() => handleReviewLeave(leave.id, 'approuve')}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-red-600"
                              onClick={() => handleReviewLeave(leave.id, 'refuse')}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Availability */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Mes disponibilités</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setAvailabilityDialogOpen(true)}>
                Modifier
              </Button>
            </CardHeader>
            <CardContent>
              {availability ? (
                <div className="space-y-2">
                  {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'].map((day) => (
                    <div key={day} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
                      <span className="capitalize text-sm font-medium">{day}</span>
                      <span className="text-sm text-slate-600">{availability[day] || '-'}</span>
                    </div>
                  ))}
                  {availability.notes && (
                    <p className="text-xs text-slate-500 mt-2 italic">Note: {availability.notes}</p>
                  )}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-4">Chargement...</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Files */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Fichiers récents</CardTitle>
            <Link to="/portail/recherche">
              <Button variant="ghost" size="sm">Voir tout</Button>
            </Link>
          </CardHeader>
          <CardContent>
            {dashboardData?.fichiers_recents?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.fichiers_recents.slice(0, 5).map((file) => (
                  <div key={file.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded-md">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.nom_fichier_original}</p>
                      <p className="text-xs text-slate-500">{file.project_nom}</p>
                    </div>
                    <Badge className={`status-${file.statut}`} variant="secondary">
                      {getStatusLabel(file.statut)}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">Aucun fichier récent</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Activité récente</CardTitle>
            <Link to="/portail/journal">
              <Button variant="ghost" size="sm">Voir tout</Button>
            </Link>
          </CardHeader>
          <CardContent>
            {dashboardData?.activites_recentes?.length > 0 ? (
              <div className="space-y-3">
                {dashboardData.activites_recentes.slice(0, 5).map((activity) => (
                  <div key={activity.id} className="flex items-start gap-3 p-2 hover:bg-slate-50 rounded-md">
                    <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-4 h-4 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">
                        <span className="font-medium">{activity.acteur_nom}</span>
                        {' '}{getEventTypeLabel(activity.type_evenement).toLowerCase()}
                        {' '}<span className="text-slate-600">{activity.fichier_nom}</span>
                      </p>
                      <p className="text-xs text-slate-500">{formatDateTime(activity.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">Aucune activité récente</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Actions rapides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Link to="/portail/clients">
              <Button variant="outline">
                <Building2 className="w-4 h-4 mr-2" />
                Gérer les clients
              </Button>
            </Link>
            <Link to="/portail/projets">
              <Button variant="outline">
                <FolderOpen className="w-4 h-4 mr-2" />
                Voir les projets
              </Button>
            </Link>
            <Link to="/portail/contacts">
              <Button variant="outline">
                <Mail className="w-4 h-4 mr-2" />
                Demandes de contact
              </Button>
            </Link>
            {isAdmin && (
              <Link to="/cms">
                <Button>
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Gestion du site web
                </Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Leave Request Dialog */}
      <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle demande de congé</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Type de congé</Label>
              <Select
                value={newLeave.type_conge}
                onValueChange={(v) => setNewLeave({ ...newLeave, type_conge: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vacances">Vacances</SelectItem>
                  <SelectItem value="maladie">Maladie</SelectItem>
                  <SelectItem value="personnel">Personnel</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date début</Label>
                <Input
                  type="date"
                  value={newLeave.date_debut}
                  onChange={(e) => setNewLeave({ ...newLeave, date_debut: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Date fin</Label>
                <Input
                  type="date"
                  value={newLeave.date_fin}
                  onChange={(e) => setNewLeave({ ...newLeave, date_fin: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Motif (optionnel)</Label>
              <Textarea
                value={newLeave.motif}
                onChange={(e) => setNewLeave({ ...newLeave, motif: e.target.value })}
                placeholder="Raison de la demande..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setLeaveDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreateLeave} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Soumettre
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Availability Dialog */}
      <Dialog open={availabilityDialogOpen} onOpenChange={setAvailabilityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier mes disponibilités</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'].map((day) => (
              <div key={day} className="flex items-center gap-4">
                <Label className="w-24 capitalize">{day}</Label>
                <Input
                  value={editAvailability[day]}
                  onChange={(e) => setEditAvailability({ ...editAvailability, [day]: e.target.value })}
                  placeholder="09:00-17:00"
                />
              </div>
            ))}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editAvailability.notes}
                onChange={(e) => setEditAvailability({ ...editAvailability, notes: e.target.value })}
                placeholder="Notes supplémentaires..."
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setAvailabilityDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleUpdateAvailability} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
